import { loadTossPayments } from '@tosspayments/payment-sdk';
import { Descriptions, Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { getPgSetting } from '../../../api/campuses.api';
import { getEventRegister } from '../../../api/events.api';
import { getEventOrderInfo } from '../../../api/payments.api';
import { cancelEventRegister } from '../../../api/refunds.api';
import { sendPaymentSuccessTodo } from '../../../api/tossPayment.api';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { campusHeadOffice } from '../../../lib/data';
import { getCommonCodeLabel } from '../../../lib/store';
import { IGetPgSetting } from '../../../models/campuses.model';
import {
  IEvent,
  IEventRegister,
  IGetEventRegister,
} from '../../../models/events.model';
import { IGetEventOrderInfo } from '../../../models/payments.model';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import Checkbox from '../../Checkbox';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import SelectCampus from '../../SelectCampus';
import SelectEmployeeModal from './modals/SelectEmployeeModal';

const Block = styled.div`
  .scroll-view {
    height: 40vh;
    overflow-y: auto;
  }
`;

interface IMatch {
  eventNo: string;
}

interface IProps {
  data: IEvent | undefined;
}

const EducationReader2: FC<IProps> = ({ data }) => {
  const { t } = useTranslation();
  const match = useRouteMatch<IMatch>();
  const eventNo = Number(match.params.eventNo);
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // PA0002 = 결제 채널
  const PA0002 = commonCodes
    .find((item) => item.commonCode === 'PA0002')
    ?.detailCode?.filter(({ detailCode }) => detailCode !== 'CP');
  const { infoNotification, successNotification, errorNotification } =
    useNotification();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetEventRegister>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetEventOrderInfo>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetPgSetting>();
  const {
    data: successRes,
    isLoading: successIsLoading,
    error: successError,
    execute: successExecute,
  } = useAxios();
  const {
    data: cancelRes,
    isLoading: cancelIsLoading,
    error: cancelError,
    execute: cancelExecute,
  } = useAxios();

  const [isCampusEligible, setIsCampusEligible] = useState(true);
  const [selectEmployeeVisible, setSelectEmployeeVisible] = useState(false);
  const [isPaymentSelectAll, setIsPaymentSelectAll] = useState(false);
  const [paymentSelects, setPaymentSelects] = useState<number[]>([]);
  const [isCancelSelectAll, setIsCancelSelectAll] = useState(false);
  const [cancelSelects, setCancelSelects] = useState<number[]>([]);
  const [mtdCode, setMtdCode] = useState('CD');
  const [orderInfo, setOrderInfo] = useState<any>();

  useEffect(() => {
    if (currentCampus && data) {
      const campusCodes = data.campuses.map((campus) => campus.campusCode);
      if (data.allCampusYn === 'Y' || campusCodes.includes(currentCampus)) {
        setIsCampusEligible(true);
        doGetEventRegister();
      } else {
        setIsCampusEligible(false);
      }
    }
  }, [currentCampus, data]);

  useEffect(() => {
    if (res2?.data && !isLoading2) {
      setOrderInfo(res2.data);
      onOpenPaymentModal(res2.data);
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (query.orderId) {
      successExecute(sendPaymentSuccessTodo(location.search));
    } else if (query.code) {
      errorNotification(t('event.education.message13'));
    }
  }, [location]);

  useEffect(() => {
    if (successRes && !successIsLoading) {
      doGetEventRegister();
      successNotification(t('event.education.message12'));
    }
  }, [successRes, successIsLoading]);

  useEffect(() => {
    if (cancelRes && !cancelIsLoading) {
      doGetEventRegister();
      successNotification(t('common.message.cancel'));
    }
  }, [cancelRes, cancelIsLoading]);

  const doGetEventRegister = () => {
    if (!currentCampus || !data) return;

    execute(
      getEventRegister('ED', data.eventNo, {
        campusCode: currentCampus,
        pageNo: 1,
        perPage: 9999999,
      }),
    );
  };

  const onSetPaymentSelects = (paymentNo: number) => {
    if (paymentSelects.includes(paymentNo)) {
      setPaymentSelects(paymentSelects.filter((item) => item !== paymentNo));
      setIsPaymentSelectAll(false);
    } else {
      setPaymentSelects([...paymentSelects, paymentNo]);
    }
  };

  const onSetPaymentSelectAll = () => {
    if (isPaymentSelectAll) {
      setPaymentSelects([]);
      setIsPaymentSelectAll(false);
    } else {
      const _selects: number[] = [];
      res?.data?.forEach((item) => {
        if (item.paymentYn === 'N') {
          _selects.push(item.eventRequireCode);
        }
      });
      setPaymentSelects(_selects);
      setIsPaymentSelectAll(true);
    }
  };

  const onSetCancelSelects = (eventRequireCode: number) => {
    if (cancelSelects.includes(eventRequireCode)) {
      setCancelSelects(
        cancelSelects.filter((item) => item !== eventRequireCode),
      );
      setIsCancelSelectAll(false);
    } else {
      setCancelSelects([...cancelSelects, eventRequireCode]);
    }
  };

  const onSetCancelSelectAll = () => {
    if (isCancelSelectAll) {
      setCancelSelects([]);
      setIsCancelSelectAll(false);
    } else {
      const _selects: number[] = [];
      res?.data?.forEach((item) => {
        if (item.cancelYn === 'N') {
          _selects.push(item.eventRequireCode);
        }
      });
      setCancelSelects(_selects);
      setIsCancelSelectAll(true);
    }
  };

  const handleHostname = (hostname: string) => {
    if (hostname === 'localhost') {
      return 'localhost:3000';
    } else {
      return hostname;
    }
  };

  const onOpenPaymentModal = async (orderInfo: any) => {
    if (!res3?.data?.onlineStoreKey) return;

    const tossPayments = await loadTossPayments(res3.data.onlineStoreKey);
    const paymentInfo = {
      amount: orderInfo.price,
      orderId: orderInfo.orderId,
      orderName: orderInfo.orderName,
      customerName: auth?.korName || '',
      successUrl: `${window.location.protocol}//${handleHostname(
        window.location.hostname,
      )}/event/education/${eventNo}`,
      failUrl: `${window.location.protocol}//${handleHostname(
        window.location.hostname,
      )}/event/education/${eventNo}`,
    };

    switch (mtdCode) {
      case 'CD':
        tossPayments.requestPayment('카드', { ...paymentInfo });
        break;
      case 'TF':
        tossPayments.requestPayment('계좌이체', { ...paymentInfo });
        break;
    }
  };

  const onGetOrderInfo = async () => {
    const paymentAmount = Number(data?.paymentAmount);
    if (!paymentAmount) return;
    if (!paymentSelects.length) {
      return infoNotification(t('event.education.message9'));
    }

    execute2(
      getEventOrderInfo({
        eventRequireCodes: paymentSelects,
        paymentMtdCode: mtdCode,
        paymentChannelCode: 'ON',
      }),
    );
    execute3(getPgSetting(campusHeadOffice));
  };

  const onCancel = () => {
    if (!cancelSelects.length) {
      return infoNotification(t('event.education.message10'));
    }

    cancelExecute(
      cancelEventRegister({
        eventRequireCode: cancelSelects.join(','),
      }),
    );
  };

  let columns: TableColumnsType<IEventRegister> = [
    {
      title: t('event.education.column22'),
      dataIndex: 'memKorName',
      align: 'center',
      sorter: (a, b) => a.memKorName?.localeCompare(b.memKorName),
    },
    {
      title: 'ID',
      dataIndex: 'webId',
      align: 'center',
      sorter: (a, b) => a.webId?.localeCompare(b.webId),
    },
    {
      title: t('event.education.column40'),
      dataIndex: 'attendStatus',
      align: 'center',
      sorter: (a, b) => a.attendStatus?.localeCompare(b.attendStatus),
      render: (value) => getCommonCodeLabel('BR0008', value, 'ko'),
    },
    {
      title: t('event.education.column28'),
      dataIndex: 'attendSaveDate',
      align: 'center',
      sorter: (a, b) => a.attendSaveDate?.localeCompare(b.attendSaveDate),
      render: (value) =>
        value ? moment(value).format('YYYY.MM.DD HH:mm') : '',
    },
    {
      title: t('event.education.column27'),
      dataIndex: 'regDate',
      align: 'center',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value) => moment(value).format('YYYY.MM.DD HH:mm'),
    },
    {
      title: t('event.education.column24'),
      dataIndex: 'paymentYn',
      align: 'center',
      sorter: (a, b) => a.paymentYn?.localeCompare(b.paymentYn),
      render: (value) =>
        value === 'Y' ? t('common.option.yes') : t('common.option.no'),
    },
    {
      title: (
        <>
          {t('event.education.column25')}{' '}
          <Checkbox
            checked={isPaymentSelectAll}
            onChange={() => {
              onSetPaymentSelectAll();
            }}
            text=""
            disabled={Number(data?.paymentAmount) === 0}
          />
        </>
      ),
      dataIndex: 'eventRequireCode',
      align: 'center',
      render: (value, record) => {
        let checked = false;
        if (
          (paymentSelects.includes(value) || isPaymentSelectAll) &&
          record.paymentYn === 'N'
        )
          checked = true;

        return (
          <Checkbox
            checked={checked}
            onChange={() => onSetPaymentSelects(value)}
            text=""
            disabled={
              record.paymentYn !== 'N' || Number(data?.paymentAmount) === 0
            }
          />
        );
      },
    },
    {
      title: (
        <>
          {t('event.education.column26')}{' '}
          <Checkbox
            checked={isCancelSelectAll}
            onChange={() => {
              onSetCancelSelectAll();
            }}
            text=""
          />
        </>
      ),
      dataIndex: 'eventRequireCode',
      align: 'center',
      render: (value, record) => {
        let checked = false;
        if (
          (cancelSelects.includes(value) || isCancelSelectAll) &&
          record.cancelYn === 'N'
        )
          checked = true;

        return (
          <Checkbox
            checked={checked}
            onChange={() => onSetCancelSelects(value)}
            text=""
            disabled={record.cancelYn === 'Y'}
          />
        );
      },
    },
  ];

  if (data && !Number(data.paymentAmount)) {
    columns = columns.slice(0, 5);
  }

  return (
    <Block className='comm-table__area rowType'>
      {(isLoading ||
        isLoading2 ||
        isLoading3 ||
        successIsLoading ||
        cancelIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={successError} />
      <ErrorResponse error={cancelError} />

      <Descriptions
        bordered
        column={4}
        size="small"
        labelStyle={{ width: '12.5%' }}
        contentStyle={{ width: '12.5%' }}
      >
        <Descriptions.Item label={t('event.education.column10')} span={4}>
          {data?.eventName}
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column12')}>
          {moment(data?.eventDate).format('YYYY.MM.DD')} {data?.eventTime}
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column7')}>
          {moment(data?.registSrtDate).format('YYYY.MM.DD')}
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column8')}>
          {moment(data?.registEndDate).format('YYYY.MM.DD')}
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column41')}>
          {Number(data?.paymentAmount) === 0
            ? t('event.education.text1')
            : `${Number(data?.paymentAmount)?.toLocaleString()} ${t(
                'event.education.text3',
              )}`}
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column15')} span={4}>
          <div dangerouslySetInnerHTML={{ __html: data?.content || '' }} />
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column17')} span={4}>
          {data?.eventFiles
            ? data?.eventFiles.map((item, index) => (
                <a
                  key={index}
                  className="download-link mr-2"
                  href={item.fileLocation}
                  download={item.originalName}
                >
                  {item.originalName}
                </a>
              ))
            : '-'}
        </Descriptions.Item>
      </Descriptions>

      <div className="flex flex-justify-end mt-4 mb-2">
        <div className="w-150p">
          <SelectCampus />
        </div>
        <Button
          onClick={() => setSelectEmployeeVisible(true)}
          className="btn btn-primary btn-w100p ml-2"
          text={t('event.education.button9')}
          disabled={!isCampusEligible}
        />
      </div>
      {isCampusEligible ? (
        <>
          <div className="scroll-view">
            <div className="inside-table-wrapper">
              <Table
                columns={columns}
                dataSource={res?.data}
                pagination={{
                  pageSize: 9999999,
                }}
                rowKey="eventRequireCode"
              />
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-3">
              <Button
                onClick={() =>
                  history.push(`/event/education/${location.search}`)
                }
                className="btn btn-primary btn-w100p ml-2"
                text={t('common.button.list')}
              />
            </div>
            <div className="col-9 flex flex-justify-end">
              {Number(data?.paymentAmount) > 0 && (
                <>
                  <div className="v-line">
                    {t('event.education.text12')}:{' '}
                    {(
                      Number(data?.paymentAmount) * paymentSelects.length
                    ).toLocaleString()}
                    {t('event.education.text13')}
                  </div>
                  <div className="w-100p mx-2 text-left">
                    <Select
                      className="w-100"
                      value={mtdCode}
                      onChange={(value) => setMtdCode(value)}
                    >
                      {PA0002?.map((item: any) => {
                        return (
                          <Select.Option
                            key={item.detailCode}
                            value={item.detailCode}
                          >
                            {item.korName}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                  <Button
                    onClick={() => {
                      orderInfo
                        ? onOpenPaymentModal(orderInfo)
                        : onGetOrderInfo();
                    }}
                    className="btn btn-primary btn-w100p"
                    text={t('event.education.column25')}
                  />
                </>
              )}
              <Button
                onClick={onCancel}
                className="btn btn-primary btn-w100p ml-2"
                text={t('event.education.column26')}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="text-center p-5">
          {t('event.education.text14')}
          <br />({t('event.education.column11')}:{' '}
          {data?.campuses?.map((item) => item.campusKorName).join(', ')})
        </div>
      )}

      {selectEmployeeVisible && (
        <SelectEmployeeModal
          visible={selectEmployeeVisible}
          setVisible={setSelectEmployeeVisible}
          eventNo={data?.eventNo}
          doGetEventRegister={doGetEventRegister}
        />
      )}
    </Block>
  );
};

export default EducationReader2;
